import React, { useState, useRef, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { Button, ButtonType, ButtonSize, Icon, IconName } from '@hills-design-system/components';
import InputText from '../InputText/InputText';
import ConfirmationModal from '../Modal/ConfirmationModal';
import Modal from '../Modal/Modal';
import { ModalSize } from '../Modal/Modal.types';
import ShelterManagement from '../ShelterManagement';
import AdminManagement from '../AdminManagement';
import SummaryAlert from '../SummaryAlert';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { useDebugActions } from '../../hooks/useDebugActions';
import { useShelterState } from '../../hooks/useShelter';
import { useShelterSwitch } from '../../hooks/useShelterSwitch';
import { ClearReportsResponse, ExportFirestoreBackupResponse } from '../../services/debug';
import { utcCalendarDateToLocal } from '../../utils/dateFormatHelpers';
import DropdownInput from '../DropdownInput/DropdownInput';
import { fetchAllSheltersWithReportCounts, ShelterWithReportCounts } from '../../services/api';
import {
  isAdminDatabaseToolsEnabled,
  isAdminDateOverrideEnabled,
  isAdminJoinDateOverrideEnabled,
  isAdminManagementEnabled,
  isAdminSentryDiagnosticsEnabled,
  isAdminShelterManagementEnabled,
  isAdminShelterSwitcherEnabled,
  isAnyAdminToolEnabled,
} from '../../utils/featureFlags';

interface AdminToolsPopoverProps {
  onDataRefresh?: () => void;
  onDateOverrideChanged?: (date: Date | null) => void;
  onJoinDateChanged?: () => void;
}

/**
 * Sections stack vertically inside a column, separated by a top border on every
 * section that has a visible section above it.
 */
const separatorClass = (...precedingSections: boolean[]) =>
  precedingSections.some(Boolean) ? 'border-t border-gray-200 pt-4' : undefined;

const ReportingModeBadge: React.FC<{ isPetszelEnabled: boolean }> = ({ isPetszelEnabled }) => (
  <span
    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
      isPetszelEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
    }`}
  >
    {isPetszelEnabled ? 'Petszel' : 'Manual'}
  </span>
);

const AdminToolsPanel: React.FC<AdminToolsPopoverProps> = ({
  onDataRefresh,
  onDateOverrideChanged,
  onJoinDateChanged,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  const [currentOverride, setCurrentOverride] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedJoinDate, setSelectedJoinDate] = useState<Date | null>(null);
  const [currentJoinOverride, setCurrentJoinOverride] = useState<Date | null>(null);
  const [isShelterManagementOpen, setIsShelterManagementOpen] = useState(false);
  const [isAdminManagementOpen, setIsAdminManagementOpen] = useState(false);
  const [clearSummary, setClearSummary] = useState<ClearReportsResponse | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<ExportFirestoreBackupResponse | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [sentryTestResult, setSentryTestResult] = useState<{
    eventId: string;
    source: 'frontend' | 'backend';
    timestamp: string;
  } | null>(null);
  const [allShelters, setAllShelters] = useState<ShelterWithReportCounts[]>([]);
  const [isLoadingShelters, setIsLoadingShelters] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);

  const shelterSwitcherEnabled = isAdminShelterSwitcherEnabled();
  const dateOverrideEnabled = isAdminDateOverrideEnabled();
  const joinDateOverrideEnabled = isAdminJoinDateOverrideEnabled();
  const shelterManagementEnabled = isAdminShelterManagementEnabled();
  const adminManagementEnabled = isAdminManagementEnabled();
  const sentryDiagnosticsEnabled = isAdminSentryDiagnosticsEnabled();
  const databaseToolsEnabled = isAdminDatabaseToolsEnabled();

  const showDateColumn = dateOverrideEnabled || joinDateOverrideEnabled;
  const showActionsColumn =
    shelterManagementEnabled ||
    adminManagementEnabled ||
    sentryDiagnosticsEnabled ||
    databaseToolsEnabled;
  const showBothColumns = showDateColumn && showActionsColumn;

  const petszelEnabledBySoldTo = useMemo(
    () => new Map(allShelters.map((s) => [s.sold_to, s.is_petszel_enabled === true])),
    [allShelters]
  );

  const popoverRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const {
    clearReports,
    getDateOverride,
    setDateOverride,
    getJoinDateOverride,
    setJoinDateOverride,
    exportFirestoreBackup,
    testSentryBackend,
  } = useDebugActions();
  const { shelter } = useShelterState();
  const { switchShelter } = useShelterSwitch();

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking inside the popover
      if (popoverRef.current?.contains(target)) {
        return;
      }

      // Don't close if confirmation modal is open or if clicking on modal elements
      if (
        isConfirmationOpen ||
        isShelterManagementOpen ||
        isAdminManagementOpen ||
        isExportModalOpen
      ) {
        return;
      }

      // Don't close if clicking on any modal backdrop or modal content
      const modalElements = document.querySelectorAll('[role="dialog"], .modal, .modal-backdrop');
      for (const element of modalElements) {
        if (element.contains(target)) {
          return;
        }
      }

      // Don't close if clicking on the datepicker calendar
      const datepickerPortal = document.getElementById('datepicker-portal');
      if (datepickerPortal?.contains(target)) {
        return;
      }

      // Don't close if clicking on any datepicker elements
      const datepickerElements = document.querySelectorAll(
        '.react-datepicker, .react-datepicker__portal, .react-datepicker-popper'
      );
      for (const element of datepickerElements) {
        if (element.contains(target)) {
          return;
        }
      }

      // Don't close if clicking on dropdown elements (portals)
      const dropdownElements = document.querySelectorAll(
        '[role="listbox"], [role="option"], [data-dropdown-menu], .dropdown-portal, .dropdown-menu'
      );
      for (const element of dropdownElements) {
        if (element.contains(target)) {
          return;
        }
      }

      // Additional check for any parent elements with dropdown attribute
      let currentElement = target as HTMLElement | null;
      while (currentElement) {
        if (
          currentElement.hasAttribute('data-dropdown-menu') ||
          currentElement.getAttribute('role') === 'listbox' ||
          currentElement.getAttribute('role') === 'option'
        ) {
          return;
        }
        currentElement = currentElement.parentElement;
      }

      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    isOpen,
    isConfirmationOpen,
    isShelterManagementOpen,
    isAdminManagementOpen,
    isExportModalOpen,
  ]);

  // Load current date override only when popover is opened.
  // This avoids unnecessary calls on initial app load.
  useEffect(() => {
    if (!isOpen || !dateOverrideEnabled) {
      return;
    }

    const loadCurrentOverride = async () => {
      try {
        const override = await getDateOverride(true);
        if (override.isActive && override.overrideDate) {
          const date = utcCalendarDateToLocal(override.overrideDate);
          setCurrentOverride(date);
          setSelectedDate(date);
        } else {
          setCurrentOverride(null);
          setSelectedDate(null);
        }
      } catch (error) {
        console.error('Failed to load date override:', error);
      }
    };
    loadCurrentOverride();
  }, [isOpen, getDateOverride, dateOverrideEnabled]);

  // Load current join date override on component mount
  useEffect(() => {
    if (!joinDateOverrideEnabled) {
      return;
    }

    const loadJoinOverride = async () => {
      try {
        const override = await getJoinDateOverride();
        if (override.isActive && override.overrideDate) {
          const date = utcCalendarDateToLocal(override.overrideDate);
          setCurrentJoinOverride(date);
          setSelectedJoinDate(date);
        }
      } catch (error) {
        console.error('Failed to load join date override:', error);
      }
    };
    loadJoinOverride();
  }, [getJoinDateOverride, joinDateOverrideEnabled]);

  // Fetch all shelters whenever popover is opened to ensure fresh data.
  useEffect(() => {
    if (!isOpen || !shelterSwitcherEnabled) {
      return;
    }

    const loadShelters = async () => {
      setIsLoadingShelters(true);
      try {
        const shelters = await fetchAllSheltersWithReportCounts();
        // Only include active shelters in the switcher dropdown.
        // Absent is_active is treated as active (matches backend validation).
        setAllShelters(shelters.filter((s) => s.is_active !== false));
      } catch (error) {
        console.error('[AdminTools] Failed to load shelters:', error);
      } finally {
        setIsLoadingShelters(false);
      }
    };
    loadShelters();
  }, [isOpen, shelterSwitcherEnabled]);

  const handleTogglePopover = () => {
    setIsOpen(!isOpen);
  };

  const handleConfirmClearDatabase = async () => {
    setIsClearing(true);
    setIsConfirmationOpen(false);
    setError(null);
    setClearSummary(null);

    try {
      const result = await clearReports();
      setClearSummary(result);
      // Don't reload automatically - let user review the summary and reload manually
    } catch (error) {
      console.error('Failed to clear database:', error);
      setError('Failed to clear database. Please try again.');
    } finally {
      setIsClearing(false);
    }
  };

  const handleReloadPage = () => {
    onDataRefresh?.();
  };

  const handleDismissSummary = () => {
    setClearSummary(null);
  };

  const handleDateChange = async (date: Date | null) => {
    setSelectedDate(date);
    setError(null);

    try {
      await setDateOverride(date);
      setCurrentOverride(date);
      onDateOverrideChanged?.(date);
      onDataRefresh?.(); // Trigger reload
    } catch (error) {
      console.error('Failed to set date override:', error);
      setError('Failed to set date override. Please try again.');
    }
  };

  const handleResetDate = async () => {
    setError(null);

    try {
      await setDateOverride(null);
      setCurrentOverride(null);
      setSelectedDate(null);
      onDateOverrideChanged?.(null);
      onDataRefresh?.(); // Trigger reload
    } catch (error) {
      console.error('Failed to reset date override:', error);
      setError('Failed to reset date override. Please try again.');
    }
  };

  const handleJoinDateChange = async (date: Date | null) => {
    setSelectedJoinDate(date);
    setError(null);

    try {
      await setJoinDateOverride(date);
      setCurrentJoinOverride(date);
      onJoinDateChanged?.();
      onDataRefresh?.(); // Trigger reload
    } catch (error) {
      console.error('Failed to set join date override:', error);
      setError('Failed to set join date override. Please try again.');
    }
  };

  const handleResetJoinDate = async () => {
    setError(null);

    try {
      await setJoinDateOverride(null);
      setCurrentJoinOverride(null);
      setSelectedJoinDate(null);
      onJoinDateChanged?.();
      onDataRefresh?.(); // Trigger reload
    } catch (error) {
      console.error('Failed to reset join date override:', error);
      setError('Failed to reset join date override. Please try again.');
    }
  };

  const formatDate = (date: Date | null) => {
    const dateToFormat = date || new Date();
    return dateToFormat.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatJoinDate = (override: Date | null, shelterDate: string | undefined) => {
    if (override) {
      return override.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    if (shelterDate) {
      const date = utcCalendarDateToLocal(shelterDate);
      if (date) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
    }
    return 'Not set';
  };

  const handleExportBackup = async () => {
    setIsExporting(true);
    setError(null);

    try {
      const result = await exportFirestoreBackup();
      setExportResult(result);
      setIsExportModalOpen(true);
    } catch (error) {
      console.error('Failed to export Firestore backup:', error);
      setError('Failed to export database backup. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyInstructions = () => {
    if (exportResult?.instructions) {
      navigator.clipboard.writeText(exportResult.instructions);
    }
  };

  const handleDownloadBackup = () => {
    if (!exportResult?.data) return;

    // Create a blob from the JSON data
    const jsonString = JSON.stringify(exportResult.data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });

    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Format filename with timestamp
    const date = new Date(exportResult.timestamp);
    const filename = `firestore-backup-${date.toISOString().split('T')[0]}.json`;
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleShelterSwitch = async (soldTo: string) => {
    if (soldTo === shelter?.sold_to) {
      return;
    }

    setIsSwitching(true);
    setError(null);

    try {
      const success = await switchShelter(soldTo);

      if (!success) {
        console.error('[AdminTools] Shelter switch failed');
        setError('Failed to switch shelter. Please try again.');
      }
    } catch (error) {
      console.error('[AdminTools] Shelter switch error:', error);
      setError('Failed to switch shelter. Please try again.');
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <>
      <div ref={popoverRef} className="fixed top-4 right-4 z-50">
        {/* Toggle Button */}
        <button
          onClick={handleTogglePopover}
          className="w-12 h-12 p-0 rounded-full shadow-lg hover:shadow-xl transition-shadow bg-white border border-gray-300 hover:bg-gray-50 flex items-center justify-center"
          disabled={isClearing}
        >
          <Icon
            icon={IconName.Settings}
            size={20}
            color={isClearing ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.8)'}
          />
        </button>

        {/* Popover Panel */}
        {isOpen && (
          <div
            className={`absolute top-16 right-0 ${
              showBothColumns ? 'w-[700px]' : 'w-[420px]'
            } bg-white rounded-lg shadow-xl border border-black/20 p-6 z-50 h-fit`}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Tools</h3>

            {/* Error Display */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Clear Summary Display */}
            {clearSummary && (
              <div className="mb-4">
                <SummaryAlert
                  title="Database Cleared Successfully"
                  description="All reports and related data have been removed from the system."
                  items={[
                    {
                      label: 'Pet Parent Reports',
                      count: clearSummary.petParentReports,
                    },
                    {
                      label: 'Adoption Reports',
                      count: clearSummary.adoptionReports,
                    },
                    {
                      label: 'Pet Parent Aggregated Data',
                      count: clearSummary.petParentAggregatedData,
                    },
                    {
                      label: 'Adoptions',
                      count: clearSummary.adoptions,
                    },
                    {
                      label: 'Orphan Pet Parents',
                      count: clearSummary.orphanPetParents,
                      isHighlight: true,
                    },
                  ]}
                  actions={[
                    {
                      label: 'Reload Page',
                      onClick: handleReloadPage,
                      variant: 'primary',
                    },
                  ]}
                  onDismiss={handleDismissSummary}
                />
              </div>
            )}

            {/* Shelter Switcher Section - Above grid */}
            {shelterSwitcherEnabled && (
              <div
                className={`mb-4 ${
                  showDateColumn || showActionsColumn ? 'pb-4 border-b border-gray-200' : ''
                }`}
              >
                <h4 className="text-sm font-medium text-gray-700 mb-2">Active Shelter</h4>
                <DropdownInput
                  id="admin-shelter-switcher"
                  label="Switch Shelter Context"
                  value={shelter?.sold_to ?? ''}
                  options={allShelters.map((s) => ({
                    id: s.sold_to,
                    label: `${s.name} (${s.sold_to})`,
                    searchText: `${s.name} ${s.sold_to} ${
                      s.is_petszel_enabled ? 'petszel' : 'manual'
                    }`,
                  }))}
                  renderOption={(option) => (
                    <span className="flex items-center gap-2">
                      <ReportingModeBadge
                        isPetszelEnabled={petszelEnabledBySoldTo.get(option.id) === true}
                      />
                      <span>{option.label}</span>
                    </span>
                  )}
                  onChange={handleShelterSwitch}
                  disabled={isSwitching}
                  canSearch={true}
                  isLoading={isLoadingShelters}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Switch to view data for a different shelter
                </p>
              </div>
            )}

            {/* Two Column Layout */}
            <div className={showBothColumns ? 'grid grid-cols-2 gap-6' : 'grid grid-cols-1'}>
              {/* Left Column - Date Management */}
              {showDateColumn && (
                <div className="space-y-4">
                  {/* Date Override Section */}
                  {dateOverrideEnabled && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Date Override for Testing
                      </h4>
                      <div className="space-y-2">
                        <InputText
                          id="date-override"
                          label="Test Date"
                          type="date"
                          value={selectedDate}
                          onChange={handleDateChange}
                          placeholder="Select date for testing"
                        />
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">
                            Current: {formatDate(currentOverride)}
                          </span>
                          <Button
                            label="Reset"
                            type={ButtonType.Secondary}
                            size={ButtonSize.Small}
                            onClick={handleResetDate}
                            disabled={!currentOverride}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Join Program Date Override Section */}
                  {joinDateOverrideEnabled && (
                    <div className={separatorClass(dateOverrideEnabled)}>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Join Date Override for Testing
                      </h4>
                      <div className="space-y-2">
                        <InputText
                          id="join-program-date"
                          label="Join Program Date"
                          type="date"
                          value={selectedJoinDate}
                          onChange={handleJoinDateChange}
                          placeholder="Select join program date"
                        />
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">
                            Current:{' '}
                            {formatJoinDate(currentJoinOverride, shelter?.join_program_date)}
                          </span>
                          <Button
                            label="Reset"
                            type={ButtonType.Secondary}
                            size={ButtonSize.Small}
                            onClick={handleResetJoinDate}
                            disabled={!currentJoinOverride}
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          Temporary override for testing. Reset clears override and uses shelter
                          join date.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Right Column - Action Buttons */}
              {showActionsColumn && (
                <div className="space-y-4">
                  {/* Shelter Management Section */}
                  {shelterManagementEnabled && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Shelter Management</h4>
                      <Button
                        label="Manage Shelters"
                        type={ButtonType.Primary}
                        size={ButtonSize.Small}
                        onClick={() => {
                          setIsShelterManagementOpen(true);
                          setIsOpen(false);
                        }}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add, edit, or remove shelters from the system
                      </p>
                    </div>
                  )}

                  {/* Admin Management Section */}
                  {adminManagementEnabled && (
                    <div className={separatorClass(shelterManagementEnabled)}>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Admin Management</h4>
                      <Button
                        label="Manage Admins"
                        type={ButtonType.Primary}
                        size={ButtonSize.Small}
                        onClick={() => {
                          setIsAdminManagementOpen(true);
                          setIsOpen(false);
                        }}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add or remove users who can access Admin Tools
                      </p>
                    </div>
                  )}

                  {/* Sentry Test Section */}
                  {sentryDiagnosticsEnabled && (
                    <div
                      className={separatorClass(shelterManagementEnabled, adminManagementEnabled)}
                    >
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Sentry Diagnostics</h4>
                      <div className="flex gap-2">
                        <Button
                          label="Test Frontend"
                          type={ButtonType.Primary}
                          destructive={true}
                          size={ButtonSize.Small}
                          onClick={() => {
                            const timestamp = new Date().toISOString();
                            let eventId = '';
                            Sentry.withScope((scope) => {
                              scope.setTag('sentry_test', 'true');
                              scope.setTag('test_source', 'frontend');
                              scope.setExtra('triggeredAt', timestamp);
                              const err = new Error(`[FSL] Sentry frontend test — ${timestamp}`);
                              eventId = Sentry.captureException(err);
                            });
                            setSentryTestResult({ eventId, source: 'frontend', timestamp });
                          }}
                          className="flex-1"
                        />
                        <Button
                          label="Test Backend"
                          type={ButtonType.Primary}
                          destructive={true}
                          size={ButtonSize.Small}
                          onClick={async () => {
                            try {
                              const { eventId, timestamp } = await testSentryBackend();
                              setSentryTestResult({ eventId, source: 'backend', timestamp });
                            } catch (error) {
                              console.error('[FSL] Sentry backend test failed:', error);
                            }
                          }}
                          className="flex-1"
                        />
                      </div>
                      {sentryTestResult && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs">
                          <p className="font-medium text-green-800">
                            ✓ {sentryTestResult.source === 'frontend' ? 'Frontend' : 'Backend'}{' '}
                            event sent
                          </p>
                          <p className="text-green-700 font-mono mt-0.5 break-all">
                            ID: {sentryTestResult.eventId}
                          </p>
                          <p className="text-green-600 mt-0.5">{sentryTestResult.timestamp}</p>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Send a tagged test exception to verify Sentry source maps on frontend and
                        backend
                      </p>
                    </div>
                  )}

                  {/* Database Management Section */}
                  {databaseToolsEnabled && (
                    <div
                      className={separatorClass(
                        shelterManagementEnabled,
                        adminManagementEnabled,
                        sentryDiagnosticsEnabled
                      )}
                    >
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Database Management
                      </h4>
                      <div className="space-y-2">
                        {/* Export Backup Button */}
                        <Button
                          label={isExporting ? 'Exporting...' : 'Export Database Backup'}
                          type={ButtonType.Secondary}
                          size={ButtonSize.Small}
                          onClick={handleExportBackup}
                          disabled={isExporting}
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500">
                          Export Firestore database for local emulator import
                        </p>

                        {/* Clear Database Button */}
                        <Button
                          label={isClearing ? 'Clearing...' : 'Clear Database'}
                          type={ButtonType.Primary}
                          destructive={true}
                          size={ButtonSize.Small}
                          onClick={() => setIsConfirmationOpen(true)}
                          disabled={isClearing}
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500">
                          Removes all reports, aggregated data, and related records for this shelter
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Shelter Management Modal */}
      {shelterManagementEnabled && (
        <ShelterManagement
          isOpen={isShelterManagementOpen}
          onClose={() => setIsShelterManagementOpen(false)}
        />
      )}

      {/* Admin Management Modal */}
      {adminManagementEnabled && (
        <AdminManagement
          isOpen={isAdminManagementOpen}
          onClose={() => setIsAdminManagementOpen(false)}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        title="Clear Database"
        message="Are you sure you want to clear all reports data? This will permanently delete all pet parent reports, adoption reports, aggregated data, adoptions, and pet parents for this shelter. This action cannot be undone."
        confirmLabel="Clear Database"
        cancelLabel={t(TranslationKeyEnum.Cancel)}
        onConfirm={handleConfirmClearDatabase}
        variant="danger"
      />

      {/* Export Backup Instructions Modal */}
      <Modal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Database Backup Export Complete"
        size={ModalSize.Large}
      >
        <div className="flex flex-col gap-4">
          {exportResult && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-green-800 mb-2">✅ Export Successful</p>
                <p className="text-sm text-gray-700">
                  <strong>Timestamp:</strong> {new Date(exportResult.timestamp).toLocaleString()}
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>Collections:</strong> shelters, pet-parents, adoptions,
                  pet-parent-reports, adoption-reports
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>Format:</strong> Compatible with seed-data.json
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-3">
                  📋 Instructions for Local Emulator Import
                </h4>
                <pre className="bg-white p-4 rounded border border-gray-300 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                  {exportResult.instructions}
                </pre>
              </div>

              <div className="flex justify-between gap-3 mt-4">
                <Button
                  label="Download JSON"
                  type={ButtonType.Primary}
                  size={ButtonSize.Medium}
                  onClick={handleDownloadBackup}
                />
                <div className="flex gap-3">
                  <Button
                    label="Copy Instructions"
                    type={ButtonType.Secondary}
                    size={ButtonSize.Medium}
                    onClick={handleCopyInstructions}
                  />
                  <Button
                    label="Close"
                    type={ButtonType.Secondary}
                    size={ButtonSize.Medium}
                    onClick={() => setIsExportModalOpen(false)}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

const AdminToolsPopover: React.FC<AdminToolsPopoverProps> = (props) => {
  if (!isAnyAdminToolEnabled()) {
    return null;
  }

  return <AdminToolsPanel {...props} />;
};

export default AdminToolsPopover;
